// Service worker registration and management

let refreshing = false;

export function registerServiceWorker(): void {
    if (!('serviceWorker' in navigator)) {
        console.log('Service workers not supported');
        return;
    }

    window.addEventListener('load', async () => {
        try {
            // Use relative path with ./ to work with base href
            const registration = await navigator.serviceWorker.register('./service-worker.js', {
                scope: './'
            });
            console.log('ServiceWorker registered:', registration.scope);

            // Handle updates
            handleServiceWorkerUpdate(registration);

            // Check for updates periodically (every 5 minutes in production)
            setInterval(() => {
                registration.update();
            }, 300000);
            
            // Also check immediately on visibility change (when app comes to foreground)
            document.addEventListener('visibilitychange', () => {
                if (document.visibilityState === 'visible') {
                    registration.update();
                }
            });

        } catch (error) {
            console.log('ServiceWorker registration failed:', error);
        }
    });

    // Listen for controller change (new SW activated)
    navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (refreshing) {
            return;
        }
        refreshing = true;
        // Use cache-busting reload to ensure fresh blazor.boot.json and assets
        // This prevents 404 errors when old cached asset references point to 
        // fingerprinted files that no longer exist after an update
        window.location.href = window.location.pathname + '?cache_bust=' + Date.now();
    });

    // Listen for messages from service worker
    navigator.serviceWorker.addEventListener('message', (event: MessageEvent) => {
        if (event.data && event.data.type === 'SYNC_SURVEYS') {
            // Trigger sync in Blazor app
            const dotNet = (window as unknown as { DotNet?: { invokeMethodAsync: (assembly: string, method: string) => void } }).DotNet;
            if (dotNet) {
                dotNet.invokeMethodAsync('MouldFlow.Surveys.Web', 'TriggerSync');
            }
        }
        
        if (event.data && event.data.type === 'APP_UPDATED') {
            console.log('[PWA] App updated to version:', event.data.version);
            // Dispatch event for Blazor components to handle
            window.dispatchEvent(new CustomEvent('pwa-version-updated', {
                detail: { version: event.data.version }
            }));
        }
        
        if (event.data && event.data.type === 'VERSION_INFO') {
            console.log('[PWA] Current version:', event.data.version, 'Cache:', event.data.cacheName);
        }
    });
}

/**
 * Handles service worker update lifecycle.
 * Detects waiting/installing workers and notifies user of available updates.
 */
function handleServiceWorkerUpdate(registration: ServiceWorkerRegistration): void {
    // Check if there's already a waiting service worker
    if (registration.waiting) {
        notifyUserOfUpdate(registration.waiting);
    }

    // Listen for new service worker installing
    registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (!newWorker) {
            return;
        }

        console.log('New service worker installing...');

        newWorker.addEventListener('statechange', () => {
            // When the new worker is installed and waiting
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                console.log('New service worker installed and waiting');
                notifyUserOfUpdate(newWorker);
            }
        });
    });
}

/**
 * Notifies the user that an update is available.
 * Shows a toast/banner with option to update now.
 */
function notifyUserOfUpdate(waitingWorker: ServiceWorker): void {
    console.log('Update available - notifying user');

    // Dispatch custom event for UI components to handle
    const updateEvent = new CustomEvent('pwa-update-available', {
        detail: {
            applyUpdate: () => applyUpdate(waitingWorker)
        }
    });
    window.dispatchEvent(updateEvent);

    // Also show a simple confirmation if no UI handler is registered
    // This can be replaced with a more elegant UI notification
    showUpdatePrompt(waitingWorker);
}

/**
 * Shows a simple update prompt to the user.
 * This can be customized or replaced with a Blazor component.
 */
function showUpdatePrompt(waitingWorker: ServiceWorker): void {
    // Check if a custom UI handler has been registered
    const customHandler = (window as unknown as { onPwaUpdateAvailable?: () => boolean }).onPwaUpdateAvailable;
    if (customHandler && customHandler()) {
        // Custom handler took over
        return;
    }

    // Create a simple toast notification
    const toast = document.createElement('div');
    toast.id = 'pwa-update-toast';
    toast.style.cssText = `
        position: fixed;
        bottom: 20px;
        left: 50%;
        transform: translateX(-50%);
        background: #1f2937;
        color: white;
        padding: 16px 24px;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        z-index: 10000;
        display: flex;
        align-items: center;
        gap: 16px;
        font-family: system-ui, -apple-system, sans-serif;
        max-width: 90vw;
    `;

    const message = document.createElement('span');
    message.textContent = 'A new version is available';

    const updateButton = document.createElement('button');
    updateButton.textContent = 'Update';
    updateButton.style.cssText = `
        background: #3b82f6;
        color: white;
        border: none;
        padding: 8px 16px;
        border-radius: 4px;
        cursor: pointer;
        font-weight: 500;
    `;
    updateButton.addEventListener('click', () => {
        toast.remove();
        applyUpdate(waitingWorker);
    });

    const dismissButton = document.createElement('button');
    dismissButton.textContent = 'Later';
    dismissButton.style.cssText = `
        background: transparent;
        color: #9ca3af;
        border: none;
        padding: 8px 16px;
        cursor: pointer;
    `;
    dismissButton.addEventListener('click', () => {
        toast.remove();
    });

    toast.appendChild(message);
    toast.appendChild(updateButton);
    toast.appendChild(dismissButton);

    // Remove any existing toast
    const existingToast = document.getElementById('pwa-update-toast');
    if (existingToast) {
        existingToast.remove();
    }

    document.body.appendChild(toast);
}

/**
 * Applies the pending update by telling the waiting worker to skip waiting.
 * The controllerchange event listener will reload the page.
 */
function applyUpdate(waitingWorker: ServiceWorker): void {
    console.log('Applying update - sending SKIP_WAITING message');
    waitingWorker.postMessage({ type: 'SKIP_WAITING' });
}

/**
 * Programmatically check for and apply updates.
 * Can be called from Blazor via JS interop.
 */
export async function checkForUpdates(): Promise<boolean> {
    if (!('serviceWorker' in navigator)) {
        return false;
    }

    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration) {
        return false;
    }

    await registration.update();
    if (registration.installing) {
        await waitForInstallation(registration.installing);
    }

    if (registration.waiting) {
        notifyUserOfUpdate(registration.waiting);
        return true;
    }

    return false;
}

function waitForInstallation(worker: ServiceWorker): Promise<void> {
    return new Promise((resolve, reject) => {
        let timeoutId: ReturnType<typeof setTimeout>;
        const complete = (error?: Error): void => {
            clearTimeout(timeoutId);
            worker.removeEventListener('statechange', onStateChange);
            if (error) reject(error);
            else resolve();
        };
        const onStateChange = (): void => {
            if (worker.state === 'installed' || worker.state === 'activated') complete();
            else if (worker.state === 'redundant') complete(new Error('App update installation failed.'));
        };

        timeoutId = setTimeout(() => complete(new Error('App update installation timed out.')), 30000);
        worker.addEventListener('statechange', onStateChange);
        onStateChange();
    });
}

/**
 * Force apply any pending update immediately.
 * Can be called from Blazor via JS interop.
 */
export async function forceUpdate(): Promise<void> {
    if (!('serviceWorker' in navigator)) {
        return;
    }

    const registration = await navigator.serviceWorker.getRegistration();
    if (registration?.waiting) {
        applyUpdate(registration.waiting);
    }
}

/**
 * Gets the current service worker version info.
 * Can be called from Blazor via JS interop.
 */
export async function getServiceWorkerVersion(): Promise<string | null> {
    if (!('serviceWorker' in navigator)) {
        return null;
    }

    const controller = navigator.serviceWorker.controller;
    if (!controller) {
        return null;
    }

    return new Promise((resolve) => {
        const messageHandler = (event: MessageEvent) => {
            if (event.data && event.data.type === 'VERSION_INFO') {
                navigator.serviceWorker.removeEventListener('message', messageHandler);
                resolve(event.data.version);
            }
        };
        
        navigator.serviceWorker.addEventListener('message', messageHandler);
        controller.postMessage({ type: 'GET_VERSION' });
        
        // Timeout after 2 seconds
        setTimeout(() => {
            navigator.serviceWorker.removeEventListener('message', messageHandler);
            resolve(null);
        }, 2000);
    });
}

// Utility to unregister all service workers and clear caches (for troubleshooting)
export async function unregisterAllServiceWorkers(): Promise<void> {
    if (!('serviceWorker' in navigator)) {
        return;
    }

    const registrations = await navigator.serviceWorker.getRegistrations();
    for (const registration of registrations) {
        await registration.unregister();
        console.log('ServiceWorker unregistered:', registration.scope);
    }

    // Clear all caches
    const cacheNames = await caches.keys();
    for (const cacheName of cacheNames) {
        await caches.delete(cacheName);
        console.log('Cache deleted:', cacheName);
    }

    console.log('All service workers and caches cleared. Please refresh the page.');
}

// Expose globally for debugging in console and Blazor interop
if (typeof window !== 'undefined') {
    const windowWithPwa = window as unknown as {
        clearServiceWorkers?: typeof unregisterAllServiceWorkers;
        checkForUpdates?: typeof checkForUpdates;
        forceUpdate?: typeof forceUpdate;
        getServiceWorkerVersion?: typeof getServiceWorkerVersion;
    };
    
    windowWithPwa.clearServiceWorkers = unregisterAllServiceWorkers;
    windowWithPwa.checkForUpdates = checkForUpdates;
    windowWithPwa.forceUpdate = forceUpdate;
    windowWithPwa.getServiceWorkerVersion = getServiceWorkerVersion;
}
